/**
 * Sales-related dashboard widgets.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSalesDocuments } from '@/hooks/use-sales';
import { useClients } from '@/hooks/use-clients';
import { Widget, TopList, HBarList, BigStat, Sparkline, MiniBars, fmtMoney, fmtDateFr, isoMonthStart, todayISO } from './common';
import type { TopListRow } from './common';

// ---- Top clients this month (by invoice total) ----

export function TopClientsWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading } = useSalesDocuments({ docType: 'invoice' });
  const { data: clients } = useClients();

  const rows: TopListRow[] = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const clientMap = new Map((clients ?? []).map((c) => [c.id, c]));
    const byClient = new Map<string, number>();
    for (const inv of invoices ?? []) {
      if (inv.docDate < monthStart) continue;
      byClient.set(inv.clientId, (byClient.get(inv.clientId) ?? 0) + inv.totalTtc);
    }
    return Array.from(byClient.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([clientId, total]) => {
        const c = clientMap.get(clientId);
        return {
          id: clientId,
          label: c?.name ?? '—',
          sublabel: c?.code,
          value: total,
          unit: 'DT',
        };
      });
  }, [invoices, clients]);

  return (
    <Widget
      title={t('dashboard.topClientsThisMonth')}
      description={t('dashboard.topClientsHelp')}
      isLoading={isLoading}
      isEmpty={rows.length === 0}
      emptyLabel={t('dashboard.noSalesThisMonth')}
    >
      <HBarList rows={rows} />
    </Widget>
  );
}

// ---- Unbilled delivery notes (BLs awaiting invoicing) ----

export function UninvoicedDeliveriesWidget() {
  const { t } = useTranslation();
  const { data: deliveries, isLoading } = useSalesDocuments({ docType: 'delivery' });

  const stat = useMemo(() => {
    const uninvoiced = (deliveries ?? []).filter((d) => !d.isInvoiced);
    const total = uninvoiced.reduce((s, d) => s + d.totalTtc, 0);
    return { count: uninvoiced.length, total };
  }, [deliveries]);

  return (
    <Widget
      title={t('dashboard.uninvoicedBLs')}
      description={t('dashboard.uninvoicedBLsHelp')}
      isLoading={isLoading}
    >
      <BigStat
        value={fmtMoney(stat.total)}
        unit="DT"
        sub={t('dashboard.uninvoicedBLsCount', { count: stat.count })}
        tone={stat.count > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}

// ---- Pipeline: quotes breakdown ----

export function SalesPipelineWidget() {
  const { t } = useTranslation();
  const { data: quotes, isLoading } = useSalesDocuments({ docType: 'quote' });
  const { data: orders } = useSalesDocuments({ docType: 'order' });

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const quotesThis = (quotes ?? []).filter((q) => q.docDate >= monthStart);
    const ordersThis = (orders ?? []).filter((o) => o.docDate >= monthStart);
    return {
      quoteCount: quotesThis.length,
      quoteTotal: quotesThis.reduce((s, q) => s + q.totalTtc, 0),
      orderCount: ordersThis.length,
      orderTotal: ordersThis.reduce((s, o) => s + o.totalTtc, 0),
    };
  }, [quotes, orders]);

  return (
    <Widget
      title={t('dashboard.pipelineBreakdown')}
      description={t('dashboard.pipelineBreakdownHelp')}
      isLoading={isLoading}
    >
      <MiniBars
        bars={[
          { label: t('nav.quotes'), value: stat.quoteTotal, color: 'oklch(0.76 0.14 70)' },
          { label: t('nav.orders'), value: stat.orderTotal, color: 'oklch(0.62 0.18 260)' },
        ]}
      />
      <p className="text-xs text-muted-foreground text-center mt-1">
        {t('dashboard.docsCount', { count: stat.quoteCount + stat.orderCount })}
      </p>
    </Widget>
  );
}

// ---- Recent invoices (last 5) ----

export function RecentInvoicesWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading } = useSalesDocuments({ docType: 'invoice' });
  const { data: clients } = useClients();

  const rows: TopListRow[] = useMemo(() => {
    const clientMap = new Map((clients ?? []).map((c) => [c.id, c]));
    return (invoices ?? [])
      .slice()
      .sort((a, b) => b.docDate.localeCompare(a.docDate))
      .slice(0, 5)
      .map((inv) => ({
        id: inv.id,
        label: inv.number,
        sublabel: `${clientMap.get(inv.clientId)?.name ?? '—'} · ${fmtDateFr(inv.docDate)}`,
        value: inv.totalTtc,
        unit: 'DT',
      }));
  }, [invoices, clients]);

  return (
    <Widget
      title={t('dashboard.recentInvoices')}
      isLoading={isLoading}
      isEmpty={rows.length === 0}
      emptyLabel={t('dashboard.noInvoices')}
    >
      <TopList rows={rows} />
    </Widget>
  );
}

// ---- Sales YTD (with 30-day sparkline) ----

export function SalesYtdWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading } = useSalesDocuments({ docType: 'invoice' });

  const stat = useMemo(() => {
    const now = new Date();
    const yearStart = `${now.getFullYear()}-01-01`;
    const ytd = (invoices ?? []).filter((i) => i.docDate >= yearStart);
    // Bucket last 30 days
    const days = 30;
    const buckets: number[] = new Array(days).fill(0);
    for (let i = 0; i < days; i++) {
      const d = new Date(now.getTime() - (days - 1 - i) * 86400000);
      const iso = d.toISOString().slice(0, 10);
      for (const inv of invoices ?? []) {
        if (inv.docDate === iso) buckets[i] = (buckets[i] ?? 0) + inv.totalTtc;
      }
    }
    return {
      total: ytd.reduce((s, i) => s + i.totalTtc, 0),
      count: ytd.length,
      spark: buckets,
    };
  }, [invoices]);

  return (
    <Widget title={t('dashboard.salesYtd')} description={t('dashboard.salesYtdHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.total)}
        unit="DT"
        sub={t('dashboard.invoicesCount', { count: stat.count })}
      />
      <div className="mt-2 -mx-2">
        <Sparkline data={stat.spark} />
      </div>
    </Widget>
  );
}

// ---- Invoices this week vs last week (with 7-day sparkline) ----

export function SalesWeeklyWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading } = useSalesDocuments({ docType: 'invoice' });

  const stat = useMemo(() => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 86400000).toISOString().slice(0, 10);
    const twoWeeksAgo = new Date(today.getTime() - 14 * 86400000).toISOString().slice(0, 10);
    const t0 = todayISO();
    const thisWeek = (invoices ?? []).filter((i) => i.docDate > weekAgo && i.docDate <= t0);
    const lastWeek = (invoices ?? []).filter((i) => i.docDate > twoWeeksAgo && i.docDate <= weekAgo);
    // 14-day sparkline showing transition from last week to this week
    const days = 14;
    const spark: number[] = new Array(days).fill(0);
    for (let i = 0; i < days; i++) {
      const d = new Date(today.getTime() - (days - 1 - i) * 86400000).toISOString().slice(0, 10);
      for (const inv of invoices ?? []) {
        if (inv.docDate === d) spark[i] = (spark[i] ?? 0) + inv.totalTtc;
      }
    }
    return {
      thisWeekTotal: thisWeek.reduce((s, i) => s + i.totalTtc, 0),
      lastWeekTotal: lastWeek.reduce((s, i) => s + i.totalTtc, 0),
      spark,
    };
  }, [invoices]);

  const diff = stat.thisWeekTotal - stat.lastWeekTotal;
  const tone = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
  const deltaPct = stat.lastWeekTotal === 0 ? '' : `${diff > 0 ? '+' : ''}${((diff / stat.lastWeekTotal) * 100).toFixed(0)}%`;

  return (
    <Widget title={t('dashboard.salesThisWeek')} description={t('dashboard.salesThisWeekHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.thisWeekTotal)}
        unit="DT"
        sub={deltaPct ? `${deltaPct} ${t('dashboard.vsLastWeek')}` : undefined}
        tone={tone}
      />
      <div className="mt-2 -mx-2">
        <Sparkline data={stat.spark} />
      </div>
    </Widget>
  );
}

// ---- Credit notes total ----

export function CreditNotesWidget() {
  const { t } = useTranslation();
  const { data: creditNotes, isLoading } = useSalesDocuments({ docType: 'credit_note' });

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    const thisMonth = (creditNotes ?? []).filter((c) => c.docDate >= monthStart);
    return {
      total: thisMonth.reduce((s, c) => s + c.totalTtc, 0),
      count: thisMonth.length,
    };
  }, [creditNotes]);

  return (
    <Widget title={t('dashboard.creditNotesMonth')} description={t('dashboard.creditNotesHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.total)}
        unit="DT"
        sub={t('dashboard.docsCount', { count: stat.count })}
        tone={stat.count > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}
